import {
    type AuditChipLabelSources,
    type AuditChipPlacement,
    entityTypeText,
    requestDataValue,
    scopeFilterLabel,
    targetFilterLabel,
    timePlacement,
    timeValue,
    translatedList,
    userFilterLabel,
} from './filterChipLabels';
import { AUDIT_VIEW_ACTION, normalizeAuditLogFilters } from './filterRules';
import { type AuditText, auditMessage, joinAuditTexts } from './text';
import type { AuditLogFilterKey, AuditLogFilters } from './types';

const CHIP_KEYS = {
    time: ['from', 'to'],
    entryId: ['entryId'],
    person: ['actorEmail'],
    actorType: ['actorType'],
    actor: ['actorUserId'],
    impersonation: ['impersonation'],
    impersonator: ['impersonatorUserId'],
    impersonatorEmail: ['impersonatorEmail'],
    actions: ['actions'],
    actionPrefix: ['action'],
    entityType: ['entityType'],
    target: ['entityId'],
    entityLabel: ['entityLabel'],
    client: ['clientId'],
    location: ['locationId'],
    cage: ['cageId'],
    outcome: ['outcome'],
    statusCode: ['statusCode'],
    requestId: ['requestId'],
    ip: ['ip'],
    userAgent: ['userAgent'],
    method: ['method'],
    route: ['route'],
    changedField: ['changedField', 'changedFrom', 'changedTo'],
    changedFrom: ['changedFrom'],
    changedTo: ['changedTo'],
    requestData: ['metadataPath', 'metadataValue'],
} as const satisfies Record<string, readonly AuditLogFilterKey[]>;

/** The two exclusion chips remove part of one list, so they have no key list of their own. */
export type AuditChipId = keyof typeof CHIP_KEYS | 'hideViews' | 'excludeActions';

export interface AuditFilterChip {
    id: AuditChipId;
    label: AuditText;
    /** Null when the label says it all, as "Hiding audit log views" does. */
    value: AuditText | null;
    placement: AuditChipPlacement;
}

type AddChip = (id: AuditChipId, value: AuditText | null, placement?: AuditChipPlacement) => void;

function whoChips(f: AuditLogFilters, sources: AuditChipLabelSources, add: AddChip): void {
    if (f.actorEmail !== undefined) add('person', f.actorEmail, 'toolbar');
    if (f.actorType !== undefined) {
        add('actorType', translatedList('auditLog.actorType', f.actorType));
    }
    const actorType = f.actorType?.[0] === 'api_user' ? 'api_user' : 'user';
    if (f.actorUserId !== undefined) {
        add('actor', userFilterLabel(actorType, f.actorUserId, sources));
    }
    if (f.impersonation !== undefined) {
        add('impersonation', auditMessage(`auditLog.chips.value.impersonation.${f.impersonation}`));
    }
    if (f.impersonatorUserId !== undefined) {
        add('impersonator', userFilterLabel('user', f.impersonatorUserId, sources));
    }
    if (f.impersonatorEmail !== undefined) add('impersonatorEmail', f.impersonatorEmail);
}

function whatChips(f: AuditLogFilters, sources: AuditChipLabelSources, add: AddChip): void {
    if (f.actions !== undefined) add('actions', joinAuditTexts(f.actions), 'toolbar');
    if (f.action !== undefined) add('actionPrefix', f.action);
    const excluded = f.excludeActions ?? [];
    if (excluded.includes(AUDIT_VIEW_ACTION)) add('hideViews', null);
    const others = excluded.filter((action) => action !== AUDIT_VIEW_ACTION);
    if (others.length > 0) add('excludeActions', joinAuditTexts(others));
    if (f.entityType !== undefined) {
        add('entityType', joinAuditTexts(f.entityType.map(entityTypeText)));
        if (f.entityId !== undefined) {
            add('target', targetFilterLabel(f.entityType[0], f.entityId, sources));
        }
    }
    if (f.entityLabel !== undefined) add('entityLabel', f.entityLabel);
}

function whereChips(f: AuditLogFilters, sources: AuditChipLabelSources, add: AddChip): void {
    if (f.clientId !== undefined) {
        add('client', scopeFilterLabel('client', f.clientId, sources), 'toolbar');
    }
    if (f.locationId !== undefined) {
        add('location', scopeFilterLabel('location', f.locationId, sources), 'toolbar');
    }
    if (f.cageId !== undefined) add('cage', scopeFilterLabel('cage', f.cageId, sources), 'toolbar');
}

function resultAndRequestChips(f: AuditLogFilters, add: AddChip): void {
    if (f.outcome !== undefined) {
        add('outcome', translatedList('auditLog.outcome', f.outcome), 'toolbar');
    }
    if (f.statusCode !== undefined) add('statusCode', String(f.statusCode));
    if (f.requestId !== undefined) add('requestId', f.requestId);
    if (f.ip !== undefined) add('ip', f.ip);
    if (f.userAgent !== undefined) add('userAgent', f.userAgent);
    if (f.method !== undefined) add('method', joinAuditTexts(f.method));
    if (f.route !== undefined) add('route', f.route);
}

function contentChips(f: AuditLogFilters, add: AddChip): void {
    if (f.changedField !== undefined) add('changedField', f.changedField);
    if (f.changedFrom !== undefined) add('changedFrom', f.changedFrom);
    if (f.changedTo !== undefined) add('changedTo', f.changedTo);
    if (f.metadataPath !== undefined) {
        add('requestData', requestDataValue(f.metadataPath, f.metadataValue));
    }
}

/**
 * One chip per active filter, in panel order. Labels for ids come from loaded rows, then
 * picker selections, then the raw "Site #57" form; see AuditChipLabelSources.
 */
export function buildAuditFilterChips(
    filters: AuditLogFilters,
    sources: AuditChipLabelSources
): AuditFilterChip[] {
    const f = normalizeAuditLogFilters(filters);
    const chips: AuditFilterChip[] = [];
    const add: AddChip = (id, value, placement = 'chips') => {
        chips.push({ id, label: auditMessage(`auditLog.chips.label.${id}`), value, placement });
    };
    if (f.from !== undefined || f.to !== undefined) {
        add('time', timeValue(f.from, f.to), timePlacement(f));
    }
    if (f.entryId !== undefined) add('entryId', f.entryId);
    whoChips(f, sources, add);
    whatChips(f, sources, add);
    whereChips(f, sources, add);
    resultAndRequestChips(f, add);
    contentChips(f, add);
    return chips;
}

/** The filters without one chip's; a chip can only remove what it shows. */
export function removeAuditFilterChip(filters: AuditLogFilters, id: AuditChipId): AuditLogFilters {
    const excluded = filters.excludeActions ?? [];
    if (id === 'hideViews') {
        return normalizeAuditLogFilters({
            ...filters,
            excludeActions: excluded.filter((action) => action !== AUDIT_VIEW_ACTION),
        });
    }
    if (id === 'excludeActions') {
        return normalizeAuditLogFilters({
            ...filters,
            excludeActions: excluded.filter((action) => action === AUDIT_VIEW_ACTION),
        });
    }
    const next: AuditLogFilters = { ...filters };
    for (const key of CHIP_KEYS[id]) delete next[key];
    return normalizeAuditLogFilters(next);
}
